import type { FC } from 'react';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { getPartyColor } from '../../pages/upload-result/party-colors';

/**
 * Party-vote column chart.
 *
 * Ported from FusionCharts to Recharts. The prop shape is unchanged — it is still
 * the FusionCharts `mscolumn2d` `{ categories, dataset }` contract — so the single
 * consumer (`view-submited-result/ResultDataTab`) and `buildChartData` needed no
 * edits. Two things went away with the port:
 *
 *  - the opaque `bg-white` div that was pinned over the FusionCharts trial credit
 *    label. Covering that mark on an unlicensed build is not something to ship,
 *    and because the cover was hardcoded white it also painted a white block over
 *    the chart's corner in dark mode;
 *  - the `@ts-ignore` on the chart element, which suppressed every type error on
 *    that JSX node rather than just the untyped default export.
 *
 * Colours come from `getPartyColor`, the same map the party bars and the
 * reconciliation views use, so a party is one colour everywhere in the app.
 * Structural colours are CSS custom properties, so they follow the theme.
 */

interface IChart {
	categories: {
		category: {
			label: string;
		}[];
	}[];
	dataset: {
		seriesname: string;
		data: {
			value: string;
		}[];
	}[];
	/** Chart height in px. */
	height?: number;
}

const AXIS = 'var(--neutral-600, #868e96)';
const GRID = 'var(--neutral-200, #e9ecef)';
const SURFACE = 'var(--neutral-0, #ffffff)';
const BORDER = 'var(--neutral-300, #dee2e6)';
const TEXT = 'var(--neutral-900, #212529)';

const Chart: FC<IChart> = ({ categories, dataset, height = 407 }) => {
	const rows = useMemo(() => {
		const labels = categories?.[0]?.category ?? [];
		return labels.map((cat, i) => ({
			label: cat.label,
			// The legacy contract sends values as strings.
			votes: Number(dataset?.[0]?.data?.[i]?.value ?? 0) || 0,
		}));
	}, [categories, dataset]);

	const seriesName = dataset?.[0]?.seriesname ?? 'Votes';

	return (
		<ResponsiveContainer
			width='100%'
			height={height}
		>
			<BarChart
				data={rows}
				margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
			>
				<CartesianGrid
					strokeDasharray='3 3'
					stroke={GRID}
					vertical={false}
				/>
				<XAxis
					dataKey='label'
					tick={{ fill: AXIS, fontSize: 12 }}
					interval={0}
					tickLine={false}
				/>
				<YAxis
					tick={{ fill: AXIS, fontSize: 12 }}
					tickFormatter={(value: number) => value.toLocaleString()}
					tickLine={false}
				/>
				<Tooltip
					formatter={(value) => [Number(value ?? 0).toLocaleString(), seriesName]}
					contentStyle={{
						backgroundColor: SURFACE,
						border: `1px solid ${BORDER}`,
						borderRadius: '4px',
						color: TEXT,
					}}
				/>
				<Bar
					dataKey='votes'
					name={seriesName}
					// No enter animation: easing a vote total up from zero adds nothing to
					// reading it.
					isAnimationActive={false}
					radius={[2, 2, 0, 0]}
				>
					{rows.map((datum) => (
						<Cell
							key={datum.label}
							fill={getPartyColor(datum.label).bg}
						/>
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

export default Chart;
